import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Typography,
  Alert,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import ReportTablePagination from '../SalesReport/ReportTable/ReportTablePagination'
import { DASHBOARD_COLORS, formatCurrency, formatNumber } from './dashboardFormatters'

const titles = {
  todaySales: "Today's Sales — Breakdown",
  monthlySales: 'Monthly Sales — Summary',
  activePartners: 'Active Partner Shops',
  pendingOrders: 'Pending Orders',
  expiringItems: 'Expiring Items',
  lowStock: 'Low Stock Items',
}

const tableHeadSx = {
  fontSize: '10.5px',
  fontWeight: 600,
  color: '#888882',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
  py: 0,
  pb: 1.25,
}

const tableCellSx = {
  fontSize: '13px',
  borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
  py: 1.125,
  px: 1,
}

const StatusBadge = ({ status }) => {
  const normalized = String(status || '').toLowerCase()
  const isCritical = normalized.includes('critical') || normalized.includes('urgent')
  const isActive = normalized.includes('active') || normalized.includes('approved')
  const isLow = normalized.includes('low') || normalized.includes('pending') || normalized.includes('inactive')

  let sx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '10.5px',
    fontWeight: 700,
    px: 1,
    py: 0.375,
    borderRadius: '20px',
  }

  if (isCritical) {
    sx = { ...sx, bgcolor: '#ffe8e8', color: '#c0392b' }
  } else if (isActive) {
    sx = { ...sx, bgcolor: '#eaf6ee', color: '#1a7a4a' }
  } else if (isLow) {
    sx = { ...sx, bgcolor: '#fff3e0', color: '#b85c00' }
  } else {
    sx = { ...sx, bgcolor: '#fff3e0', color: '#b85c00' }
  }

  return <Box component="span" sx={sx}>{status}</Box>
}

const ExpirySeverityDot = ({ days }) => {
  const d = Number(days) || 0
  const color = d <= 7 ? '#e74c3c' : d <= 15 ? '#f59300' : '#27ae60'
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        display: 'inline-block',
        mr: 0.75,
        flexShrink: 0,
      }}
    />
  )
}

const expiryDaysColor = (days) => {
  const d = Number(days) || 0
  if (d <= 7) return '#c0392b'
  if (d <= 15) return '#b85c00'
  return '#1a7a4a'
}

const KpiDetailModal = ({
  open,
  onClose,
  detailKey,
  rows = [],
  loading = false,
  error = null,
  onRetry,
  pagination = null,
  onPageChange,
  onRowsPerPageChange,
  searchValue = '',
  onSearchChange,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '12px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        maxHeight: '80vh',
      },
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.75,
        py: 2.25,
        borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
        fontSize: '15px',
        fontWeight: 700,
      }}
    >
      {titles[detailKey] || 'Details'}
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        sx={{
          border: '1px solid rgba(0,0,0,0.13)',
          borderRadius: '8px',
          bgcolor: DASHBOARD_COLORS.bg,
          color: '#555550',
          '&:hover': { bgcolor: '#fff1f3', color: DASHBOARD_COLORS.red },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ px: 2.75, py: 2.25 }}>
      <Stack direction="row" sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={
            detailKey === 'pendingOrders'
              ? 'Search by shop or item…'
              : 'Search in this list…'
          }
          value={searchValue}
          onChange={(event) => onSearchChange?.(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#888882' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Typography
                component="button"
                type="button"
                onClick={onRetry}
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  bgcolor: 'transparent',
                  font: 'inherit',
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                Retry
              </Typography>
            ) : null
          }
        >
          {error}
        </Alert>
      ) : (
        <>
          <Table size="small" sx={{ '& tbody tr:hover td': { bgcolor: DASHBOARD_COLORS.bg } }}>
            <TableHead>
              <TableRow>
                {detailKey === 'lowStock' && (
                  <>
                    <TableCell sx={tableHeadSx}>Product</TableCell>
                    <TableCell sx={tableHeadSx}>Category</TableCell>
                    <TableCell sx={tableHeadSx}>Stock</TableCell>
                    <TableCell sx={tableHeadSx}>Status</TableCell>
                  </>
                )}
              {(detailKey === 'todaySales' || detailKey === 'monthlySales') && (
                <>
                  <TableCell sx={tableHeadSx}>Partner Shop</TableCell>
                  <TableCell sx={tableHeadSx}>Sales</TableCell>
                  <TableCell sx={tableHeadSx}>Orders</TableCell>
                </>
              )}
                {detailKey === 'pendingOrders' && (
                  <>
                    <TableCell sx={tableHeadSx}>Shop / Outlet</TableCell>
                    <TableCell sx={tableHeadSx}>Items</TableCell>
                    <TableCell sx={tableHeadSx}>Amount</TableCell>
                    <TableCell sx={tableHeadSx}>State</TableCell>
                  </>
                )}
                {detailKey === 'expiringItems' && (
                  <>
                    <TableCell sx={tableHeadSx}>Product</TableCell>
                    <TableCell sx={tableHeadSx}>Category</TableCell>
                    <TableCell sx={tableHeadSx}>Units</TableCell>
                    <TableCell sx={tableHeadSx}>Expires In</TableCell>
                  </>
                )}
                {detailKey === 'activePartners' && (
                  <>
                    <TableCell sx={tableHeadSx}>Partner Shop</TableCell>
                    <TableCell sx={tableHeadSx}>Status</TableCell>
                    <TableCell sx={tableHeadSx}>Last Order</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  {detailKey === 'lowStock' && (
                    <>
                      <TableCell sx={tableCellSx}>{row.product}</TableCell>
                      <TableCell sx={tableCellSx}>{row.category}</TableCell>
                      <TableCell sx={tableCellSx}>{formatNumber(row.stock)}</TableCell>
                      <TableCell sx={tableCellSx}><StatusBadge status={row.status} /></TableCell>
                    </>
                  )}
                  {(detailKey === 'todaySales' || detailKey === 'monthlySales') && (
                    <>
                      <TableCell sx={tableCellSx}>{row.name}</TableCell>
                      <TableCell sx={tableCellSx}>{formatCurrency(row.sales)}</TableCell>
                      <TableCell sx={tableCellSx}>{formatNumber(row.orders)}</TableCell>
                    </>
                  )}
                  {detailKey === 'pendingOrders' && (
                    <>
                      <TableCell sx={tableCellSx}>{row.name}</TableCell>
                      <TableCell sx={tableCellSx}>{row.items}</TableCell>
                      <TableCell sx={tableCellSx}>{formatCurrency(row.amount)}</TableCell>
                      <TableCell sx={tableCellSx}><StatusBadge status={row.status} /></TableCell>
                    </>
                  )}
                  {detailKey === 'expiringItems' && (
                    <>
                      <TableCell sx={tableCellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ExpirySeverityDot days={row.days} />
                          {row.name}
                        </Box>
                      </TableCell>
                      <TableCell sx={tableCellSx}>{row.category}</TableCell>
                      <TableCell sx={tableCellSx}>{formatNumber(row.units)}</TableCell>
                      <TableCell sx={{ ...tableCellSx, color: expiryDaysColor(row.days), fontWeight: 700 }}>
                        {row.days} days
                      </TableCell>
                    </>
                  )}
                  {detailKey === 'activePartners' && (
                    <>
                      <TableCell sx={tableCellSx}>{row.name}</TableCell>
                      <TableCell sx={tableCellSx}><StatusBadge status={row.status} /></TableCell>
                      <TableCell sx={tableCellSx}>{row.lastOrder}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ ...tableCellSx, borderBottom: 'none' }}>
                    <Typography variant="body2" color="text.secondary">No records found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {pagination && (
            <ReportTablePagination
              pagination={pagination}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              compact
            />
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
)

export default KpiDetailModal
